import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  doNotAskWhenAgainWhenAcceptingMergeProposalAtom,
  selectedMergeProposalAtom,
  useRefreshIntraIdSection,
} from "../state";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import MergeProposalCleaningArea from "./MergeProposalCleaningArea";
import ItemSelector from "./ItemSelector";
import { useSessionApiClient } from "../api/sessions";
import Spinner from "./Spinner";
import React, { useEffect, useState } from "react";
import { FINALIZED_REVIEW_STATUS, NEEDS_REVIEW_STATUS } from "../constants";
import ConfirmationDialog from "./ConfirmationDialog";
import ImageSizeSlider from "./ImageSizeSlider";

const renderContent = (
  mergeProposals,
  selectedMergeProposal,
  setSelectedMergeProposal,
  sessionId,
  fetchingMergeProposals,
  reviewStatusFilterValue
) => {
  if (mergeProposals.length === 0 && !fetchingMergeProposals) {
    return (
      <Box sx={{ margin: "1em" }}>
        <Alert severity="warning">
          <Typography>
            No merge proposals with review status {reviewStatusFilterValue} were
            found.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <>
      {mergeProposals.length > 0 && (
        <Grid container spacing={1} sx={{ margin: 0 }}>
          <Grid item xs={2}>
            <Paper elevation={5}>
              <ItemSelector
                items={mergeProposals}
                selectedItem={selectedMergeProposal}
                setSelectedItem={setSelectedMergeProposal}
                itemIdProp={"id"}
                renderItem={(mergeProposal) => (
                  <Typography>{`Proposal: ${mergeProposal.id}`}</Typography>
                )}
              />
            </Paper>
          </Grid>
          {
            <Grid item xs={10}>
              <Paper elevation={0}>
                <React.Suspense
                  fallback={<Spinner message={"Loading subject images ..."} />}
                >
                  <MergeProposalCleaningArea
                    mergeProposal={selectedMergeProposal}
                    sessionId={sessionId}
                  />
                </React.Suspense>
              </Paper>
            </Grid>
          }
        </Grid>
      )}
    </>
  );
};

const InterIdCleaning = ({ sessionId }) => {
  const [mergeProposals, setMergeProposals] = useState([]);
  const [fetchingMergeProposals, setFetchingMergeProposals] = useState(true);
  const selectedMergeProposal = useRecoilValue(selectedMergeProposalAtom);
  const setSelectedMergeProposal = useSetRecoilState(selectedMergeProposalAtom);
  const apiClient = useSessionApiClient();
  const refreshIntraIdSection = useRefreshIntraIdSection();
  const [reviewStatusFilterValue, setReviewStatusFilterValue] =
    useState(NEEDS_REVIEW_STATUS);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [
    doNotAskWhenAgainWhenAcceptingMergeProposal,
    setDoNotAskWhenAgainWhenAcceptingMergeProposal,
  ] = useRecoilState(doNotAskWhenAgainWhenAcceptingMergeProposalAtom);

  const onReviewFilterStatusChange = (e, value) => {
    setSelectedMergeProposal({ id: "" });
    setMergeProposals([]);
    setReviewStatusFilterValue(value);
  };

  useEffect(() => {
    setFetchingMergeProposals(true);
    const fetchMergeProposals = async () => {
      let mergeProposals = [];

      if (apiClient && sessionId) {
        mergeProposals =
          await apiClient.getMergeProposalsWithSubjectNamesFromCurrentSession(
            sessionId,
            reviewStatusFilterValue
          );
      }

      setMergeProposals(mergeProposals);
    };

    fetchMergeProposals().finally(() => {
      setFetchingMergeProposals(false);
    });
  }, [reviewStatusFilterValue]);

  const acceptMergeProposal = async () => {
    setOpenConfirmationDialog(false);
    await apiClient.acceptMergeProposal(sessionId, selectedMergeProposal.id);

    setMergeProposals(
      mergeProposals.filter(
        (proposal) => proposal.id !== selectedMergeProposal.id
      )
    );

    // Accepting a merge operation has the side effect of potentially reducing the list of subjects, plus updating
    // the list of images for a given subject. This side effect impacts the Intra-ID section who is responsible for
    // subject-level operations. Because of this we need signal the section that it should be refreshed. Under the
    // hood we're using the approach described here https://recoiljs.org/docs/guides/asynchronous-data-queries#use-a-request-id
    // to force a reevaluation of the necessary selectors and atom families.
    // *NOTE*: This does not re-render the Intra-ID upon call of this function. It just makes the necessary changes
    // so that it is re-rendered next time is visible.
    refreshIntraIdSection();
  };

  const onIntentToAcceptMergeProposal = async () => {
    if (doNotAskWhenAgainWhenAcceptingMergeProposal) {
      await acceptMergeProposal();
    } else {
      setOpenConfirmationDialog(true);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, margin: "1em" }}>
      {selectedMergeProposal?.id !== undefined && (
        <ConfirmationDialog
          title={"Confirmation required"}
          content={`Accepting a merge proposal is an irreversible operation. Are you sure you want to accept proposal ${selectedMergeProposal.id}?`}
          open={openConfirmationDialog}
          onClose={() => setOpenConfirmationDialog(false)}
          onAccept={acceptMergeProposal}
          doNotAskAgain={doNotAskWhenAgainWhenAcceptingMergeProposal}
          onDoNotAskAgainPreferenceChanged={(newValue) =>
            setDoNotAskWhenAgainWhenAcceptingMergeProposal(newValue)
          }
        />
      )}
      <Box>
        <Grid container spacing={1} sx={{ margin: 0 }}>
          <Grid item xs={4}>
            <FormControl spacing={1}>
              <RadioGroup
                row
                aria-labelledby="review-status-radio-buttons-group-label"
                value={reviewStatusFilterValue}
                name="review-status-radio-buttons-group"
                onChange={onReviewFilterStatusChange}
              >
                <FormControlLabel
                  value={NEEDS_REVIEW_STATUS}
                  control={<Radio />}
                  label="Proposals to review"
                />
                <FormControlLabel
                  value={FINALIZED_REVIEW_STATUS}
                  control={<Radio />}
                  label="Finalized proposals (Read-Only)"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid
            item
            xs={4}
            alignItems="center"
            display="flex"
            justifyContent="center"
          >
            {selectedMergeProposal?.id !== undefined &&
              mergeProposals.length > 0 &&
              reviewStatusFilterValue === NEEDS_REVIEW_STATUS && (
                <Box>
                  <Button
                    variant={"contained"}
                    onClick={onIntentToAcceptMergeProposal}
                  >
                    Accept Proposal {selectedMergeProposal.id}
                  </Button>
                </Box>
              )}
          </Grid>
          <Grid item xs={4}>
            {mergeProposals.length > 0 && <ImageSizeSlider />}
          </Grid>
        </Grid>
      </Box>

      {renderContent(
        mergeProposals,
        selectedMergeProposal,
        setSelectedMergeProposal,
        sessionId,
        fetchingMergeProposals,
        reviewStatusFilterValue
      )}
    </Box>
  );
};

export default InterIdCleaning;
